import Box from "@mui/material/Box";
import Image from "next/image";
import { Divider, Typography } from "@mui/material";
import Maybe from "../../../../components/common/Maybe";
import { Images, Company } from "@brand";
import PhoneIcon from "components/icons/PhoneIcon";
import EnvelopeIcon from "components/icons/EnvelopeIcon";
import WebIcon from "components/icons/WebIcon";
import ContactLink from "components/common/ContactLink";
import NextLink from "next/link";
import { Link as MUILink } from "@mui/material";
import { partnerLink } from "utilities/partners";
import { forgetTrackingParams } from "utilities/tracking";

interface Props {
  brand: ContentfulAgent;
  partner: ContentfulPartner | null;
}

const PARTNER_LOGO_BOX = { width: 220, height: 72 };

const fitToBox = (logo: ContentfulImage) => {
  const scale = Math.min(
    PARTNER_LOGO_BOX.width / logo.width,
    PARTNER_LOGO_BOX.height / logo.height
  );

  return {
    width: Math.round(logo.width * scale),
    height: Math.round(logo.height * scale),
  };
};

const Logobar = ({ brand, partner }: Props): JSX.Element => {
  const linkIconWrapperStyling = {
    height: { xs: "34px", sm: "42px" },
    width: { xs: "34px", sm: "42px" },
    aspectRatio: "1",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  };

  const partnerLogo = partner?.logo;
  const partnerLogoSize = partnerLogo ? fitToBox(partnerLogo) : null;
  const homeHref = partner ? partnerLink(partner, "") : "/";

  // A partner without a logo is otherwise invisible, and its code lives in a
  // cookie for a month — long enough to strand someone who only opened the
  // link once, especially when that partner cannot sell any plan.
  const leavePartner = () => {
    forgetTrackingParams();
    window.location.assign("/");
  };

  return (
    <Box
      display={"flex"}
      justifyContent={"space-between"}
      alignItems={"center"}
      width={1}
    >
      <Box
        sx={{
          display: "flex",
          gap: { xs: "8px", sm: "16px" },
          alignItems: "center",
          minWidth: 0,
        }}
      >
        <Maybe test={partnerLogo}>
          <Box
            sx={{ display: { xs: "none", sm: "flex" } }}
            alignItems={"center"}
          >
            <NextLink href={homeHref} passHref>
              <MUILink underline="none" component="a">
                <Image
                  src={`${partnerLogo?.url}?h=${
                    (partnerLogoSize?.height as number) * 2
                  }`}
                  alt={partnerLogo?.title || (partner?.name as string)}
                  width={partnerLogoSize?.width}
                  height={partnerLogoSize?.height}
                />
              </MUILink>
            </NextLink>
          </Box>

          <Divider orientation="vertical" flexItem />
        </Maybe>

        <Box
          component={"div"}
          display={"flex"}
          alignItems={"center"}
          sx={{
            // The brand logos differ in width by a factor of three, so the
            // narrow screen gets a ceiling rather than a size.
            "& img": {
              maxWidth: { xs: "150px", sm: "none" },
              height: "auto",
            },
          }}
        >
          <NextLink href={homeHref} passHref>
            <MUILink underline="none" component="a">
              <Image
                src={Images.logo.src}
                alt={Images.logo.alt}
                width={Images.logo.width}
                height={Images.logo.height}
              />
            </MUILink>
          </NextLink>
        </Box>

        <Maybe test={partner}>
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              maxWidth: "200px",
              lineHeight: 1.2,
            }}
          >
            <Typography variant={"caption"} color="text.secondary">
              Referred by {partner?.name}, insurance provided by {Company.name}
            </Typography>
            <MUILink
              component="button"
              type="button"
              variant={"caption"}
              underline="hover"
              sx={{ alignSelf: "flex-start" }}
              onClick={leavePartner}
            >
              Not from {partner?.name}?
            </MUILink>
          </Box>
        </Maybe>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: { xs: "4px", sm: "8px" },
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <ContactLink href={`tel:${brand.phone}`} sx={{ boxShadow: "none" }}>
          <Box sx={linkIconWrapperStyling}>
            <PhoneIcon />
          </Box>
        </ContactLink>
        <ContactLink href={`mailto:${brand.email}`} sx={{ boxShadow: "none" }}>
          <Box sx={linkIconWrapperStyling}>
            <EnvelopeIcon />
          </Box>
        </ContactLink>
        <ContactLink
          href={brand.mtiWebpage}
          target="_blank"
          sx={{ boxShadow: "none" }}
        >
          <Box sx={linkIconWrapperStyling}>
            <WebIcon />
          </Box>
        </ContactLink>
      </Box>
    </Box>
  );
};

export default Logobar;
